import React, { useEffect, useState } from "react";
import "../assets/styles/CMDB.scss";
import ComputerIcon from "@mui/icons-material/Computer";
import RouterIcon from "@mui/icons-material/Router";
import CloudIcon from "@mui/icons-material/Cloud";
import MiscellaneousServicesIcon from "@mui/icons-material/MiscellaneousServices";
import ViewInArIcon from "@mui/icons-material/ViewInAr";
import FilterListIcon from "@mui/icons-material/FilterList";
import RefreshIcon from "@mui/icons-material/Refresh";

const API = "http://localhost:8000";

interface Tag { id: number; name: string; }
interface Environment { id: number; name: string; color: string; }
interface Asset {
  id: number;
  name: string;
  type: "vm" | "container" | "network" | "cloud" | "service";
  status: "up" | "down" | "maintenance" | "unknown";
  environment?: Environment;
  ip_address?: string;
  os?: string;
  provider?: string;
  description?: string;
  tags: Tag[];
  created_at: string;
}
interface Stats {
  total: number;
  by_type: Record<string, number>;
  by_status: Record<string, number>;
  by_environment: Record<string, number>;
}

const TYPE_ICONS: Record<string, React.ReactNode> = {
  vm:        <ComputerIcon fontSize="small" />,
  container: <ViewInArIcon fontSize="small" />,
  network:   <RouterIcon fontSize="small" />,
  cloud:     <CloudIcon fontSize="small" />,
  service:   <MiscellaneousServicesIcon fontSize="small" />,
};

const TYPE_LABELS: Record<string, string> = {
  vm: "VM", container: "Container", network: "Réseau", cloud: "Cloud", service: "Service",
};

const STATUS_LABELS: Record<string, string> = {
  up: "UP", down: "DOWN", maintenance: "MAINTENANCE", unknown: "INCONNU",
};

function AssetCard({ asset }: { asset: Asset }) {
  return (
    <div className={`cmdb-card cmdb-card--${asset.status}`}>
      <div className="cmdb-card-header">
        <div className="cmdb-card-type">
          {TYPE_ICONS[asset.type]}
          <span>{TYPE_LABELS[asset.type]}</span>
        </div>
        <span className={`cmdb-status cmdb-status--${asset.status}`}>
          <span className="cmdb-status-dot" />
          {STATUS_LABELS[asset.status]}
        </span>
      </div>

      <h3 className="cmdb-card-name">{asset.name}</h3>

      {asset.description && (
        <p className="cmdb-card-desc">{asset.description}</p>
      )}

      <div className="cmdb-card-meta">
        {asset.environment && (
          <span
            className="cmdb-env-badge"
            style={{ borderColor: asset.environment.color, color: asset.environment.color }}
          >
            {asset.environment.name}
          </span>
        )}
        {asset.ip_address && <span className="cmdb-meta-item">&#x1F4CD; {asset.ip_address}</span>}
        {asset.provider && <span className="cmdb-meta-item">{asset.provider}</span>}
        {asset.os && <span className="cmdb-meta-item">{asset.os}</span>}
      </div>

      {asset.tags.length > 0 && (
        <div className="cmdb-card-tags">
          {asset.tags.map((tag) => (
            <span key={tag.id} className="cmdb-tag">{tag.name}</span>
          ))}
        </div>
      )}
    </div>
  );
}

function StatBox({ label, value, accent }: { label: string; value: number; accent?: string }) {
  return (
    <div className="cmdb-stat-box" style={{ borderColor: accent }}>
      <span className="cmdb-stat-value" style={{ color: accent }}>{value}</span>
      <span className="cmdb-stat-label">{label}</span>
    </div>
  );
}

function CMDB() {
  const [assets, setAssets] = useState<Asset[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [environments, setEnvironments] = useState<Environment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [filterType, setFilterType] = useState<string>("all");
  const [filterStatus, setFilterStatus] = useState<string>("all");
  const [filterEnv, setFilterEnv] = useState<string>("all");

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (filterType !== "all") params.set("type", filterType);
      if (filterStatus !== "all") params.set("status", filterStatus);
      if (filterEnv !== "all") params.set("environment_id", filterEnv);

      const [assetsRes, statsRes, envsRes] = await Promise.all([
        fetch(`${API}/assets/?${params}`),
        fetch(`${API}/assets/stats`),
        fetch(`${API}/environments/`),
      ]);

      setAssets(await assetsRes.json());
      setStats(await statsRes.json());
      setEnvironments(await envsRes.json());
    } catch {
      setError("Impossible de contacter l'API CMDB. Vérifiez que le backend tourne sur le port 8000.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [filterType, filterStatus, filterEnv]);

  const types = ["all", "vm", "container", "network", "cloud", "service"];
  const statuses = ["all", "up", "down", "maintenance", "unknown"];

  return (
    <section className="cmdb-section">
      <div className="cmdb-header">
        <div>
          <h1>Infrastructure CMDB</h1>
          <p className="cmdb-subtitle">
            Inventaire des assets infra — VMs, réseaux, cloud, services
          </p>
        </div>
        <button className="cmdb-refresh-btn" onClick={fetchData} title="Actualiser">
          <RefreshIcon fontSize="small" />
        </button>
      </div>

      {error && (
        <div className="cmdb-unavailable">
          <span className="cmdb-unavailable-icon">⚠</span>
          <p>L'API n'est pas disponible</p>
        </div>
      )}

      {!error && stats && (
        <div className="cmdb-stats-row">
          <StatBox label="Total" value={stats.total} accent="var(--accent)" />
          <StatBox label="UP" value={stats.by_status.up ?? 0} accent="#34d399" />
          <StatBox label="DOWN" value={stats.by_status.down ?? 0} accent="#ef4444" />
          <StatBox label="Maintenance" value={stats.by_status.maintenance ?? 0} accent="#f59e0b" />
          <StatBox label="VMs" value={stats.by_type.vm ?? 0} />
          <StatBox label="Cloud" value={stats.by_type.cloud ?? 0} />
          <StatBox label="Services" value={stats.by_type.service ?? 0} />
          <StatBox label="Réseau" value={stats.by_type.network ?? 0} />
        </div>
      )}

      {!error && (
        <>
          <div className="cmdb-filters">
            <FilterListIcon fontSize="small" style={{ color: "var(--text-muted)" }} />

            <div className="cmdb-filter-group">
              {types.map((t) => (
                <button
                  key={t}
                  className={`cmdb-filter-btn${filterType === t ? " active" : ""}`}
                  onClick={() => setFilterType(t)}
                >
                  {t === "all" ? "Tous" : TYPE_LABELS[t]}
                </button>
              ))}
            </div>

            <div className="cmdb-filter-group">
              {statuses.map((s) => (
                <button
                  key={s}
                  className={`cmdb-filter-btn cmdb-filter-btn--status cmdb-filter-btn--${s}${filterStatus === s ? " active" : ""}`}
                  onClick={() => setFilterStatus(s)}
                >
                  {s === "all" ? "Tout statut" : STATUS_LABELS[s]}
                </button>
              ))}
            </div>

            <select
              className="cmdb-filter-select"
              value={filterEnv}
              onChange={(e) => setFilterEnv(e.target.value)}
            >
              <option value="all">Tous les environnements</option>
              {environments.map((env) => (
                <option key={env.id} value={env.id}>{env.name}</option>
              ))}
            </select>
          </div>

          {loading ? (
            <div className="cmdb-grid">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="cmdb-card cmdb-card--skeleton" />
              ))}
            </div>
          ) : (
            <>
              <p className="cmdb-count">{assets.length} asset{assets.length !== 1 ? "s" : ""}</p>
              <div className="cmdb-grid">
                {assets.map((asset) => (
                  <AssetCard key={asset.id} asset={asset} />
                ))}
              </div>
              {assets.length === 0 && (
                <div className="cmdb-empty">Aucun asset ne correspond aux filtres sélectionnés.</div>
              )}
            </>
          )}
        </>
      )}
    </section>
  );
}

export default CMDB;
